/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import _ from 'underscore';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { TextField, Grid } from '@mui/material';
import { SelectField, FilePicker } from 'components';
import DateTimePicker from 'components/DatePicker/DateTimePicker.js';
import { SetFormState, GetTypes, GetTeamsByType } from '../../../../../../actions/TicketsActions.js';

const schema = {
	subject: {
		presence: { allowEmpty: false, message: 'Subject is required' },
	},
	typeId: {
		presence: { allowEmpty: false, message: 'Type is required' },
	},
	activityId: {
		presence: { allowEmpty: false, message: 'Activity is required' },
	},
	targetExecutionDate: {
		presence: { allowEmpty: false, message: 'Target Execution Date is required' },
	},
};

const useStyles = makeStyles((theme) => ({
	root: {},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
}));

const AddEditUserForm = (props) => {
	const { className, types, teams, activities, ...rest } = props;

	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.ticketsReducer.formState);

	const _usePrevious = (value) => {
		const ref = useRef();
		useEffect(() => {
			ref.current = value;
		});
		return ref.current;
	};

	const _useHasChanged = (val) => {
		const prevVal = _usePrevious(val);
		return prevVal !== val;
	};

	const hasActivityChanged = _useHasChanged(formState.values.activityId);
	const hasTypeChanged = _useHasChanged(formState.values.typeId);

	useEffect(() => {
		const newTouched = {};
		if (formState.values.submit) {
			_.each(_.keys(schema), (item => {
				newTouched[item] = true;
			}));
		}

		const errors = validate(formState.values, schema, { fullMessages: false });

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
			touched: {
				...formState.touched,
				...newTouched
			},
		});

		if (hasActivityChanged) {
			dispatch(GetTypes(formState.values.activityId, 1));
		}

		if (hasTypeChanged && formState.values.typeId !== '') {
			dispatch(GetTeamsByType(formState.values.typeId, 1));
		}
	}, [formState.values]);

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleChangeActivity = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				typeId: '',
				teamId: '',
				activityId: event.target.value,
			},
			touched: {
				...formState.touched,
				activityId: true,
			},
		});
	};

	const _handleDateTimeChange = (value) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				targetExecutionDate: value,
			},
			touched: {
				...formState.touched,
				targetExecutionDate: true,
			},
		});
	};

	const _onChangeInput = (event) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setFormState({
				...formState,
				values: {
					...formState.values,
					selectedFile: file,
				},
				touched: {
					...formState.touched,
					selectedFile: true,
				},
			});
		}
	};

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	return (
		<div className={classes.fields}>
			<Grid container spacing={4}>
				<Grid item md={6} xs={12}>
					<TextField
						error={_hasError('subject')}
						fullWidth
						helperText={
							_hasError('subject') ? formState.errors.subject[0] : null
						}
						label="Subject"
						name="subject"
						onChange={_handleChange}
						value={formState.values.subject || ''}
						variant="outlined"
					/>
				</Grid>
				<Grid item md={6} xs={12}>
					<DateTimePicker
						label="Target Execution Date"
						value={formState.values.targetExecutionDate}
						onChange={_handleDateTimeChange}
						error={_hasError('targetExecutionDate')}
						helperText={_hasError('targetExecutionDate') ? formState.errors.targetExecutionDate[0] : null}
					/>
				</Grid>
				<Grid item md={4} xs={12}>
					<SelectField
						id="activity"
						name="activityId"
						value={formState.values.activityId || ''}
						onChange={_handleChangeActivity}
						hintText="Activity"
						menuItems={activities}
						error={_hasError('activityId') ? formState.errors.activityId[0] : null}
					/>
				</Grid>
				<Grid item md={4} xs={12}>
					<SelectField
						id="type"
						name="typeId"
						value={formState.values.typeId || ''}
						onChange={_handleChange}
						hintText="Type"
						menuItems={types}
						error={_hasError('typeId') ? formState.errors.typeId[0] : null}
					/>
				</Grid>
				<Grid item md={4} xs={12}>
					<SelectField
						id="team"
						name="teamId"
						value={formState.values.teamId || ''}
						onChange={_handleChange}
						hintText="Team"
						menuItems={teams}
						disabled
						error={_hasError('teamId') ? formState.errors.teamId[0] : null}
					/>
				</Grid>
				<Grid item md={12} xs={12}>
					<FilePicker value={formState.values.selectedFile?.name} onChange={_onChangeInput} accept="*" />
				</Grid>
				<Grid item md={12} xs={12}>
					<TextField
						error={_hasError('comment')}
						fullWidth
						helperText={
							_hasError('comment') ? formState.errors.comment[0] : null
						}
						label="Comment"
						name="comment"
						onChange={_handleChange}
						value={formState.values.comment || ''}
						variant="outlined"
						multiline
						rows={3}
					/>
				</Grid>
			</Grid>
		</div>

	);
};

AddEditUserForm.propTypes = {
	className: PropTypes.string,
	types: PropTypes.array.isRequired,
	teams: PropTypes.array.isRequired,
	activities: PropTypes.array.isRequired,
};

export default AddEditUserForm;
